import {useLocalStorage, useViewer} from 'bigdatr-style';
import React, {createContext, useContext, useState} from 'react';
import {TableColumn} from '~/creative/affordance/types';
import {useCountry} from '~/feature/country/CountryContext';
import {AU} from 'bigdatr-style';
import useHideCategoryForNz from '~/feature/filter/useHideCategoryForNz';
import useNewsItems from '~/feature/notifications/news/useNewsItems';
import {MediaValuePageArgs} from '~/pages/explore-media-value/types';

type GlobalState = {
    news: {hasUnreadNews: boolean; setLastReadNews: (next: string) => void};
    headerSearch: {
        debouncedSearch: string;
        setDebouncedSearch: (next: string) => void;
    };
    tableColumns: {
        allAvailableColumns: TableColumn[];
        defaultTableColumns: TableColumn[];
        tableColumns: TableColumn[];
        setTableColumns: (next: TableColumn[]) => void;
        // at the moment these 2 are in a POC/prototype stage. in the future they would likely be
        // merged with `tableColumns`
        allAvailableComplianceColumns: TableColumn[];
        complianceColumns: TableColumn[];
        setComplianceColumns: (next: TableColumn[]) => void;
    };
    mediaExpenditureState: {
        setLastState: (next: MediaValuePageArgs | null) => void;
        lastState: MediaValuePageArgs | null;
        downloadPercentageOnly: boolean;
        setDownloadPercentageOnly: (next: boolean) => void;
    };
};
const GlobalStateContext = createContext<GlobalState | undefined>(undefined);

const allAustralianTableColumns: TableColumn[] = [
    TableColumn.aiAdType,
    TableColumn.aiAdvertisers,
    TableColumn.aiIndustries,
    TableColumn.aiCategories,
    TableColumn.aiProducts,
    TableColumn.campaignName,
    TableColumn.country,
    TableColumn.firstAppeared,
    TableColumn.lastAppeared,
    TableColumn.mediaType,
    TableColumn.mediaOwner,
    TableColumn.publisher,
    TableColumn.region,
    TableColumn.status,
    TableColumn.ads
];

const allAvailableComplianceTableColumns = [
    TableColumn.aiAdType,
    TableColumn.aiAdvertisers,
    TableColumn.aiIndustries,
    TableColumn.aiCategories,
    TableColumn.aiProducts,
    TableColumn.firstAppeared,
    TableColumn.lastAppeared,
    TableColumn.mediaType,
    TableColumn.mediaOwner,
    TableColumn.publisher,
    TableColumn.region,
    TableColumn.complianceRanking,
    TableColumn.complianceReason
];

const defaultComplianceTableColumns = [
    TableColumn.aiAdvertisers,
    TableColumn.aiIndustries,
    TableColumn.firstAppeared,
    TableColumn.lastAppeared,
    TableColumn.mediaType,
    TableColumn.complianceRanking,
    TableColumn.complianceReason
];

const defaultAustralianTableColumns = [
    TableColumn.aiAdType,
    TableColumn.aiAdvertisers,
    TableColumn.aiIndustries,
    TableColumn.aiCategories,
    TableColumn.aiProducts,
    TableColumn.country,
    TableColumn.firstAppeared,
    TableColumn.lastAppeared,
    TableColumn.mediaType,
    TableColumn.mediaOwner,
    TableColumn.publisher,
    TableColumn.status,
    TableColumn.campaignName,
    TableColumn.ads
];

const allNonAustralianTableColumns = [
    TableColumn.advertiserDomain,
    TableColumn.country,
    TableColumn.firstAppeared,
    TableColumn.lastAppeared,
    TableColumn.mediaType,
    TableColumn.publication,
    TableColumn.status,
    TableColumn.campaignName,
    TableColumn.ads,
    TableColumn.aiAdType,
    TableColumn.aiAdvertisers,
    TableColumn.aiIndustries,
    TableColumn.aiCategories,
    TableColumn.aiProducts
];

// @intent - store any global state here that you need to reuse across different pages and component
export function GlobalStateProvider(props: {children: React.ReactNode}) {
    const {currentTeamId} = useViewer();

    //
    // news page stuff
    const [lastReadNews, setLastReadNews] = useLocalStorage('bd-app-lastReadNotification');
    const newsItems = useNewsItems();

    //
    // header search
    const [debouncedSearch, setDebouncedSearch] = useState('');

    //
    // table columns
    const {country} = useCountry();
    const hideCategoryForNz = useHideCategoryForNz();

    const [australianColumns, setAustralianColumns] = useLocalStorage<TableColumn[]>(
        `bd-app-australianTableColumns-v2-${currentTeamId}`
    );
    const safeAustralianColumns = australianColumns ?? defaultAustralianTableColumns;

    const [nonAustralianColumns, setNonAustralianColumns] = useLocalStorage<TableColumn[]>(
        `bd-app-nonAustralianTableColumns-v2-${currentTeamId}`
    );
    const safeNonAustralianColumns = nonAustralianColumns ?? allNonAustralianTableColumns;

    const [complianceColumns, setComplianceColumns] = useLocalStorage<TableColumn[]>(
        `bd-app-complianceTableColumns-v3-${currentTeamId}`
    );
    const safeComplianceColumns = complianceColumns ?? defaultComplianceTableColumns;

    //Media expenditure state
    const [lastState, setLastState] = useLocalStorage<MediaValuePageArgs>(
        `bd-app-lastExpenditureReportState-v3-team-${country}-${currentTeamId}`
    );

    const [localStorageDownloadPercentageOnly, setDownloadPercentageOnly] =
        useLocalStorage<boolean>(
            // remembers selections per team, per country
            `bd-app-downloadPercentageOnly-${currentTeamId}-${country}`
        );

    function getData(): GlobalState {
        let hasUnreadNews = true;
        if (lastReadNews != null) {
            hasUnreadNews = new Date(lastReadNews) < newsItems[0].date;
        }

        let allAvailableColumns: TableColumn[] = allAustralianTableColumns;
        let defaultTableColumns: TableColumn[] = defaultAustralianTableColumns;
        let tableColumns: TableColumn[] = safeAustralianColumns;

        let setTableColumns = setAustralianColumns;
        if (country !== AU) {
            allAvailableColumns = allNonAustralianTableColumns;
            defaultTableColumns = allNonAustralianTableColumns;
            tableColumns = safeNonAustralianColumns;
            if (hideCategoryForNz) {
                allAvailableColumns = allAvailableColumns.filter(
                    (col) => col !== TableColumn.aiCategories
                );
                defaultTableColumns = defaultTableColumns.filter(
                    (col) => col !== TableColumn.aiCategories
                );
                tableColumns = tableColumns.filter((col) => col !== TableColumn.aiCategories);
            }
            setTableColumns = setNonAustralianColumns;
        }

        return {
            news: {
                hasUnreadNews,
                setLastReadNews: (next: string) => {
                    setLastReadNews(next);
                }
            },
            headerSearch: {
                debouncedSearch,
                setDebouncedSearch
            },
            tableColumns: {
                allAvailableColumns,
                defaultTableColumns,
                tableColumns,
                setTableColumns,
                allAvailableComplianceColumns: allAvailableComplianceTableColumns,
                complianceColumns: safeComplianceColumns,
                setComplianceColumns
            },
            mediaExpenditureState: {
                lastState,
                setLastState,
                downloadPercentageOnly: localStorageDownloadPercentageOnly ?? false,
                setDownloadPercentageOnly
            }
        };
    }

    return (
        <GlobalStateContext.Provider value={getData()}>
            {props.children}
        </GlobalStateContext.Provider>
    );
}

export function useGlobalState() {
    const globalState = useContext(GlobalStateContext);
    if (!globalState) throw new Error('GlobalStateContext is not yet initialized');
    return globalState;
}
